import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { RegisterDto } from '@assetaccountant/api/models/register.dto';
import { ListAvailableIntegrationsResponse } from '@assetaccountant/integrations/models/common/list-available-integrations-response';
import { ListRegisterIntegrationsResponse } from '@assetaccountant/integrations/models/common/list-register-integrations-response';
import { IntegrationType } from '@assetaccountant/integrations/models/integration-type';
import { environment } from '../../../../environments/environment';
import { EnabledAndDisabledPools } from '../components/pool-setting-selection/model/enabled-pools';
import { ListRegisterPermissionsResponse } from '../contract/authorization/list-register-permissions.response';
import { ListRegisterRolesResponse } from '../contract/authorization/list-register-roles-response';
import { ListRegisterUsersResponse } from '../contract/authorization/list-register-users.response';
import { REGISTER_PLACEHOLDER } from '../http-token.interceptor';
import { BulkUpdateAssetGroupDepreciationCommand } from '../models/bulk-update-asset-group-depreciation-command';
import { BulkUpsertGlAccountsCommand } from '../models/bulk-upsert-gl-accounts-command';
import { ListCustomFieldKeysResponse } from '../models/list-custom-field-keys-response';
import { ListTagsResponse } from '../models/list-tags-response';
import { RegimeApiModel } from '../models/regime-api-model';
import { Register } from '../models/register';
import { UpdateRegisterCommand } from '../operations/update-register-command';
import { UpdateRegisterResponse } from '../operations/update-register-response';
import { CompareFn, SORT_COMPARE } from './compare.factory';
import { ModalService } from './modal.service';

@Injectable({
  providedIn: 'root',
})
export class RegisterService {
  constructor(
    private http: HttpClient,
    private modalService: ModalService,
    @Inject(SORT_COMPARE) private sortCompare: CompareFn,
  ) {}

  recordAccess(registerId: number) {
    const url = `${environment.servicesApiUrl}/api/v2/registers/${registerId}/access`;

    return this.http.get(url);
  }

  updateRegister(
    register: UpdateRegisterCommand,
    registerId?: number,
  ): Observable<UpdateRegisterResponse> {
    return this.http.put<UpdateRegisterResponse>(
      `${environment.servicesApiUrl}/api/v2/registers/${
        registerId ? registerId : REGISTER_PLACEHOLDER
      }`,
      register,
    );
  }

  getRegimes(registerId: number): Observable<RegimeApiModel[]> {
    return this.http.get<RegimeApiModel[]>(
      `${environment.servicesApiUrl}/api/v2/registers/${registerId}/regimes`,
    );
  }

  getFirstDayOfCurrentFinancialYear(): Observable<string> {
    return this.http.get<string>(`${environment.servicesApiUrl}/api/register/finyear-start`);
  }

  getRegister(registerId?: number) {
    // Temporary measure - need to tidy up API models
    return this.http.get<Register>(
      `${environment.servicesApiUrl}/api/register`,
      registerId
        ? {
            headers: {
              RegisterId: registerId.toString(),
            },
          }
        : undefined,
    );
  }

  getPoolSettings(registerId: number): Observable<EnabledAndDisabledPools> {
    return this.http.get<EnabledAndDisabledPools>(
      `${environment.servicesApiUrl}/api/register/${registerId}/pools`,
    );
  }

  savePoolSettings(
    registerId: number,
    model: EnabledAndDisabledPools,
  ): Observable<EnabledAndDisabledPools> {
    return this.http.put<EnabledAndDisabledPools>(
      `${environment.servicesApiUrl}/api/register/${registerId}/pools`,
      model,
    );
  }

  export(registerId: number) {
    return this.http.get(`${environment.servicesApiUrl}/api/register/${registerId}/export`);
  }

  listTags(): Observable<ListTagsResponse> {
    return this.http.get<ListTagsResponse>(
      `${environment.servicesApiUrl}/api/v2/registers/${REGISTER_PLACEHOLDER}/tags`,
    );
  }

  setTags(tagValueIds: string[]): Observable<ListTagsResponse> {
    return this.http.put<ListTagsResponse>(
      `${environment.servicesApiUrl}/api/v2/registers/${REGISTER_PLACEHOLDER}/tags`,
      { tagValueIds },
    );
  }

  ignoreDraftAssets(externalReferenceNos: string[], integrationType?: IntegrationType) {
    const queryString = integrationType ? `?integrationType=${integrationType}` : '';
    return this.http.request(
      'delete',
      `${environment.servicesApiUrl}/api/v2/registers/${REGISTER_PLACEHOLDER}/draftAssets${queryString}`,
      {
        body: { externalReferenceNos },
      },
    );
  }

  restoreDraftAssets(
    externalReferenceNos: string[],
    integrationType?: IntegrationType,
  ): Observable<any> {
    const queryString = integrationType ? `?integrationType=${integrationType}` : '';
    return this.http.put<void>(
      `${environment.servicesApiUrl}/api/v2/registers/${REGISTER_PLACEHOLDER}/draftAssets${queryString}`,
      { externalReferenceNos },
    );
  }

  getCustomFieldKeys(): Observable<ListCustomFieldKeysResponse> {
    return this.http.get<ListCustomFieldKeysResponse>(
      `${environment.servicesApiUrl}/api/v3/registers/${REGISTER_PLACEHOLDER}/customfieldkeys`,
    );
  }

  deleteCustomFieldKey(key: string) {
    return this.http.delete(
      `${environment.servicesApiUrl}/api/v3/registers/${REGISTER_PLACEHOLDER}/customfieldkey/${key}`,
    );
  }

  userContinueAfterCheckUsageEvent(command: any) {
    return this.checkUsage(command).pipe(
      switchMap(res => {
        if (res.length > 0) {
          return this.modalService
            .showQuestionModal(
              '<h4>The following asset(s) are using units of use depreciation method, but have no usage records during the selected period. Do you want to continue?</h4>' +
                '<table class="table"><thead><tr><th>Asset Name</th><th>Group Name</th></tr></thead>' +
                '<tbody>' +
                res.map(r => `<tr><td>${r.assetName}</td><td>${r.groupName}</td></tr>`).join('') +
                '</tbody></table>',
              'Warning',
              'Yes',
              'Cancel',
              true,
            )
            .pipe(
              map(res2 => {
                return res2 === 'OK';
              }),
            );
        } else {
          return of(true);
        }
      }),
    );
  }

  checkUsage(command: any) {
    return this.http.post<any>(
      environment.servicesApiUrl + `/api/v3/registers/${REGISTER_PLACEHOLDER}/check-usage`,
      command,
    );
  }

  listRoles(registerId?: number) {
    return this.http.get<ListRegisterRolesResponse>(
      `${environment.servicesApiUrl}/api/v2/registers/${registerId || REGISTER_PLACEHOLDER}/roles`,
    );
  }

  listUsers(registerId?: number, includeInvited = false) {
    return this.http.get<ListRegisterUsersResponse>(
      `${environment.servicesApiUrl}/api/v2/registers/${
        registerId || REGISTER_PLACEHOLDER
      }/users?includeInvited=${includeInvited}`,
    );
  }

  removeUser(userId: string) {
    return this.http.delete(
      `${environment.servicesApiUrl}/api/v2/registers/${REGISTER_PLACEHOLDER}/users/${userId}`,
    );
  }

  /*
  updateUser(
    user: {
      userId: string;
      registerRoleId: number;
      isActive: boolean;
    },
    registerId?: number,
  ) {
    return this.http.put<void>(
      `${environment.servicesApiUrl}/api/v2/registers/${
        registerId ? registerId : REGISTER_PLACEHOLDER
      }/users/${user.userId}`,
      user,
    );
  }
  */

  updateUser(registerId: number, email: string, registerRoleId: number) {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v2/registers/${
        registerId ? registerId : REGISTER_PLACEHOLDER
      }/user`,
      { email, registerRoleId },
    );
  }

  updateUsers(registerId: number, emails: string[], registerRoleId: number) {
    return this.http.post(
      `${environment.servicesApiUrl}/api/v2/registers/${
        registerId ? registerId : REGISTER_PLACEHOLDER
      }/users`,
      { emails, registerRoleId },
    );
  }

  listPermissions() {
    return this.http.get<ListRegisterPermissionsResponse>(
      `${environment.servicesApiUrl}/api/v2/registers/${REGISTER_PLACEHOLDER}/permissions`,
    );
  }

  listIntegrations() {
    return this.http.get<ListRegisterIntegrationsResponse>(
      `${environment.servicesApiUrl}/api/v2/registers/${REGISTER_PLACEHOLDER}/integrations`,
    );
  }

  listAvailableIntegrations(forDraftAssets = false) {
    return this.http.get<ListAvailableIntegrationsResponse>(
      `${environment.servicesApiUrl}/api/v2/registers/${REGISTER_PLACEHOLDER}/available-integrations?forDraftAssets=${forDraftAssets}`,
    );
  }

  bulkUpdateAssetGroupDepreciation(command: BulkUpdateAssetGroupDepreciationCommand) {
    return this.http.put(
      `${environment.servicesApiUrl}/api/v3/registers/${REGISTER_PLACEHOLDER}/assetgroup-depreciation`,
      command,
    );
  }

  bulkUpsertGlAccounts(command: BulkUpsertGlAccountsCommand) {
    return this.http.put(
      `${environment.servicesApiUrl}/api/v3/registers/${REGISTER_PLACEHOLDER}/glAccounts`,
      command,
    );
  }

  sortRegisters(registers: RegisterDto[]) {
    return registers.sort((a, b) => {
      const registerNameComparison = this.sortCompare(a.name, b.name);
      if (registerNameComparison !== 0) {
        return registerNameComparison;
      }
      return this.sortCompare(a.entityName, b.entityName);
    });
  }
}
